import React, { useEffect, useState } from 'react';
import { getErrorMessage } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  CreditCard,
  Wallet,
  Building2,
  Settings2,
  Save,
  RefreshCw,
  Key,
  Check,
  AlertCircle,
  Eye,
  EyeOff,
  TestTube,
  Globe,
  Shield,
  Banknote,
  CircleDollarSign
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface PaymentGatewayConfig {
  enabled: boolean;
  mode: 'test' | 'live';
  api_key?: string;
  secret_key?: string;
  public_key?: string;
  client_id?: string;
  client_secret?: string;
  merchant_id?: string;
  terminal_id?: string;
  webhook_secret?: string;
  supported_currencies: string[];
  supported_methods: string[];
}

interface PaymentSettings {
  default_gateway: string;
  default_currency: string;
  stripe: PaymentGatewayConfig;
  paypal: PaymentGatewayConfig;
  nexi: PaymentGatewayConfig;
  satispay: PaymentGatewayConfig;
  bonifico: PaymentGatewayConfig;
}

const defaultSettings: PaymentSettings = {
  default_gateway: 'stripe',
  default_currency: 'EUR',
  stripe: {
    enabled: false,
    mode: 'test',
    public_key: '',
    secret_key: '',
    webhook_secret: '',
    supported_currencies: ['EUR', 'USD', 'GBP'],
    supported_methods: ['card', 'sepa_debit']
  },
  paypal: {
    enabled: false,
    mode: 'test',
    client_id: '',
    client_secret: '',
    supported_currencies: ['EUR', 'USD', 'GBP'],
    supported_methods: ['paypal', 'card']
  },
  nexi: {
    enabled: false,
    mode: 'test',
    api_key: '',
    secret_key: '',
    merchant_id: '',
    terminal_id: '',
    supported_currencies: ['EUR'],
    supported_methods: ['card', 'bancomat']
  },
  satispay: {
    enabled: false,
    mode: 'test',
    api_key: '',
    supported_currencies: ['EUR'],
    supported_methods: ['satispay']
  },
  bonifico: {
    enabled: false,
    mode: 'live',
    supported_currencies: ['EUR'],
    supported_methods: ['bank_transfer']
  }
};

const AdminPaymentsPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<PaymentSettings>(defaultSettings);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});
  const [testing, setTesting] = useState<string | null>(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/payment-settings`);
      setSettings({ ...defaultSettings, ...res.data });
    } catch (error) {
      console.error('Error fetching payment settings:', error);
      // Se non esistono impostazioni, usa quelle di default
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/admin/payment-settings`, settings);
      toast.success('Impostazioni di pagamento salvate');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(getErrorMessage(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  const testGateway = async (gateway: string) => {
    try {
      setTesting(gateway);
      const res = await axios.post(`${API}/admin/payment-settings/test/${gateway}`);
      if (res.data.success) {
        toast.success(`${gateway}: Connessione riuscita!`);
      } else {
        toast.error(`${gateway}: ${res.data.error || 'Test fallito'}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, `Errore test ${gateway}`));
    } finally {
      setTesting(null);
    }
  };

  const toggleSecret = (key: string) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const updateGateway = (gateway: string, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [gateway]: {
        ...prev[gateway as keyof PaymentSettings] as PaymentGatewayConfig,
        [field]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const SecretInput = ({ 
    gateway, 
    field, 
    label, 
    placeholder 
  }: { 
    gateway: string; 
    field: string; 
    label: string; 
    placeholder: string;
  }) => {
    const key = `${gateway}_${field}`;
    const value = (settings[gateway as keyof PaymentSettings] as PaymentGatewayConfig)?.[field as keyof PaymentGatewayConfig] as string || '';
    
    return (
      <div className="space-y-2">
        <Label>{label}</Label>
        <div className="relative">
          <Input
            type={showSecrets[key] ? 'text' : 'password'}
            value={value}
            onChange={(e) => updateGateway(gateway, field, e.target.value)}
            placeholder={placeholder}
            className="pr-10"
            data-testid={`${gateway}-${field}-input`}
          />
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-0 top-0 h-full px-3"
            onClick={() => toggleSecret(key)}
          >
            {showSecrets[key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    );
  };

  const GatewayHeader = ({ 
    gateway, 
    icon: Icon, 
    title, 
    description 
  }: { 
    gateway: string; 
    icon: any; 
    title: string; 
    description: string;
  }) => {
    const config = settings[gateway as keyof PaymentSettings] as PaymentGatewayConfig;
    
    return (
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${config?.enabled ? 'bg-green-100 dark:bg-green-900' : 'bg-muted'}`}>
              <Icon className={`h-5 w-5 ${config?.enabled ? 'text-green-600' : 'text-muted-foreground'}`} />
            </div>
            <div>
              <CardTitle className="flex items-center gap-2">
                {title}
                {config?.enabled && (
                  <Badge variant={config.mode === 'live' ? 'default' : 'secondary'}>
                    {config.mode === 'live' ? 'LIVE' : 'TEST'}
                  </Badge>
                )}
              </CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Switch
              checked={config?.enabled || false}
              onCheckedChange={(checked) => updateGateway(gateway, 'enabled', checked)}
              data-testid={`${gateway}-enabled-switch`}
            />
          </div>
        </div>
      </CardHeader>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-payments-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Gestione Pagamenti</h1>
          <p className="text-muted-foreground mt-1">
            Configura i gateway di pagamento per la piattaforma
          </p>
        </div>
        <Button onClick={saveSettings} disabled={saving} data-testid="save-payment-settings-btn">
          {saving ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
          Salva Impostazioni
        </Button>
      </div>

      {/* Default Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Impostazioni Generali
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label>Gateway Predefinito</Label>
            <Select 
              value={settings.default_gateway} 
              onValueChange={(v) => setSettings(prev => ({ ...prev, default_gateway: v }))}
            >
              <SelectTrigger data-testid="default-gateway-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="stripe">Stripe</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="nexi">Nexi</SelectItem>
                <SelectItem value="satispay">Satispay</SelectItem>
                <SelectItem value="bonifico">Bonifico Bancario</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Valuta Predefinita</Label>
            <Select 
              value={settings.default_currency} 
              onValueChange={(v) => setSettings(prev => ({ ...prev, default_currency: v }))}
            >
              <SelectTrigger data-testid="default-currency-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="EUR">Euro (EUR)</SelectItem>
                <SelectItem value="USD">Dollaro USA (USD)</SelectItem>
                <SelectItem value="GBP">Sterlina (GBP)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="stripe" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="stripe" className="gap-2" data-testid="tab-stripe">
            <CreditCard className="h-4 w-4" />
            Stripe
          </TabsTrigger>
          <TabsTrigger value="paypal" className="gap-2" data-testid="tab-paypal">
            <Wallet className="h-4 w-4" />
            PayPal
          </TabsTrigger>
          <TabsTrigger value="nexi" className="gap-2" data-testid="tab-nexi">
            <Building2 className="h-4 w-4" />
            Nexi
          </TabsTrigger>
          <TabsTrigger value="satispay" className="gap-2" data-testid="tab-satispay">
            <CircleDollarSign className="h-4 w-4" />
            Satispay
          </TabsTrigger>
          <TabsTrigger value="bonifico" className="gap-2" data-testid="tab-bonifico">
            <Banknote className="h-4 w-4" />
            Bonifico
          </TabsTrigger>
        </TabsList>

        {/* TAB STRIPE */}
        <TabsContent value="stripe" className="space-y-6">
          <Card>
            <GatewayHeader
              gateway="stripe"
              icon={CreditCard}
              title="Stripe"
              description="Carte di credito, SEPA, Apple Pay, Google Pay"
            />
            <CardContent className="space-y-6">
              {settings.stripe?.enabled && (
                <>
                  <div className="space-y-2">
                    <Label>Modalità</Label>
                    <Select 
                      value={settings.stripe.mode} 
                      onValueChange={(v) => updateGateway('stripe', 'mode', v)}
                    >
                      <SelectTrigger data-testid="stripe-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="test">
                          <span className="flex items-center gap-2">
                            <TestTube className="h-4 w-4" />
                            Test Mode
                          </span>
                        </SelectItem>
                        <SelectItem value="live">
                          <span className="flex items-center gap-2">
                            <Globe className="h-4 w-4" />
                            Live Mode
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <SecretInput
                      gateway="stripe"
                      field="public_key"
                      label="Publishable Key"
                      placeholder="pk_test_... o pk_live_..."
                    />
                    <SecretInput
                      gateway="stripe"
                      field="secret_key"
                      label="Secret Key"
                      placeholder="sk_test_... o sk_live_..."
                    />
                  </div>

                  <SecretInput
                    gateway="stripe"
                    field="webhook_secret"
                    label="Webhook Secret"
                    placeholder="whsec_..."
                  />

                  <div className="flex items-center gap-4 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => testGateway('stripe')}
                      disabled={testing === 'stripe'}
                      data-testid="test-stripe-btn"
                    >
                      {testing === 'stripe' ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <TestTube className="mr-2 h-4 w-4" />
                      )}
                      Test Connessione
                    </Button>
                    <a 
                      href="https://dashboard.stripe.com/apikeys" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      Ottieni le chiavi API →
                    </a>
                  </div>
                </>
              )}

              {!settings.stripe?.enabled && (
                <div className="text-center py-8 text-muted-foreground">
                  <CreditCard className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Abilita Stripe per configurare i pagamenti con carta</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB PAYPAL */}
        <TabsContent value="paypal" className="space-y-6">
          <Card>
            <GatewayHeader
              gateway="paypal"
              icon={Wallet}
              title="PayPal"
              description="Pagamenti PayPal e carte via PayPal"
            />
            <CardContent className="space-y-6">
              {settings.paypal?.enabled && (
                <>
                  <div className="space-y-2">
                    <Label>Modalità</Label>
                    <Select 
                      value={settings.paypal.mode} 
                      onValueChange={(v) => updateGateway('paypal', 'mode', v)}
                    >
                      <SelectTrigger data-testid="paypal-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="test">Sandbox (Test)</SelectItem>
                        <SelectItem value="live">Live (Produzione)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <SecretInput
                      gateway="paypal"
                      field="client_id"
                      label="Client ID"
                      placeholder="Client ID PayPal"
                    />
                    <SecretInput
                      gateway="paypal"
                      field="client_secret"
                      label="Client Secret"
                      placeholder="Client Secret PayPal"
                    />
                  </div>

                  <div className="flex items-center gap-4 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => testGateway('paypal')}
                      disabled={testing === 'paypal'}
                      data-testid="test-paypal-btn"
                    >
                      {testing === 'paypal' ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <TestTube className="mr-2 h-4 w-4" />
                      )}
                      Test Connessione
                    </Button>
                    <a 
                      href="https://developer.paypal.com/dashboard/applications" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      Ottieni le credenziali →
                    </a>
                  </div>
                </>
              )}

              {!settings.paypal?.enabled && (
                <div className="text-center py-8 text-muted-foreground">
                  <Wallet className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Abilita PayPal per accettare pagamenti PayPal</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB NEXI */}
        <TabsContent value="nexi" className="space-y-6">
          <Card>
            <GatewayHeader
              gateway="nexi"
              icon={Building2}
              title="Nexi"
              description="Gateway POS italiano per carte e Bancomat"
            />
            <CardContent className="space-y-6">
              {settings.nexi?.enabled && (
                <>
                  <div className="space-y-2">
                    <Label>Modalità</Label>
                    <Select 
                      value={settings.nexi.mode} 
                      onValueChange={(v) => updateGateway('nexi', 'mode', v)}
                    >
                      <SelectTrigger data-testid="nexi-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="test">Test</SelectItem>
                        <SelectItem value="live">Produzione</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <SecretInput
                      gateway="nexi"
                      field="api_key"
                      label="API Key"
                      placeholder="API Key Nexi"
                    />
                    <SecretInput
                      gateway="nexi"
                      field="secret_key"
                      label="Secret Key"
                      placeholder="Secret Key Nexi"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Merchant ID (Alias)</Label>
                      <Input
                        value={settings.nexi.merchant_id || ''}
                        onChange={(e) => updateGateway('nexi', 'merchant_id', e.target.value)}
                        placeholder="Alias Nexi"
                        data-testid="nexi-merchant-id-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Terminal ID</Label>
                      <Input
                        value={settings.nexi.terminal_id || ''}
                        onChange={(e) => updateGateway('nexi', 'terminal_id', e.target.value)}
                        placeholder="Terminal ID"
                        data-testid="nexi-terminal-id-input"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-4 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => testGateway('nexi')}
                      disabled={testing === 'nexi'}
                      data-testid="test-nexi-btn"
                    >
                      {testing === 'nexi' ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <TestTube className="mr-2 h-4 w-4" />
                      )}
                      Test Connessione
                    </Button>
                    <a 
                      href="https://developer.nexi.it/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      Documentazione Nexi →
                    </a>
                  </div>
                </>
              )}

              {!settings.nexi?.enabled && (
                <div className="text-center py-8 text-muted-foreground">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Abilita Nexi per accettare pagamenti POS</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB SATISPAY */}
        <TabsContent value="satispay" className="space-y-6">
          <Card>
            <GatewayHeader
              gateway="satispay"
              icon={CircleDollarSign}
              title="Satispay"
              description="Pagamenti mobile Satispay"
            />
            <CardContent className="space-y-6">
              {settings.satispay?.enabled && (
                <>
                  <div className="space-y-2">
                    <Label>Modalità</Label>
                    <Select 
                      value={settings.satispay.mode} 
                      onValueChange={(v) => updateGateway('satispay', 'mode', v)}
                    >
                      <SelectTrigger data-testid="satispay-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="test">Sandbox</SelectItem>
                        <SelectItem value="live">Produzione</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <SecretInput
                    gateway="satispay"
                    field="api_key"
                    label="API Key"
                    placeholder="Bearer Token Satispay"
                  />

                  <div className="flex items-center gap-4 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => testGateway('satispay')}
                      disabled={testing === 'satispay'}
                      data-testid="test-satispay-btn"
                    >
                      {testing === 'satispay' ? (
                        <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <TestTube className="mr-2 h-4 w-4" />
                      )}
                      Test Connessione
                    </Button>
                    <a 
                      href="https://developers.satispay.com/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      Documentazione Satispay →
                    </a>
                  </div>
                </>
              )}

              {!settings.satispay?.enabled && (
                <div className="text-center py-8 text-muted-foreground">
                  <CircleDollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Abilita Satispay per accettare pagamenti mobile</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB BONIFICO */}
        <TabsContent value="bonifico" className="space-y-6">
          <Card>
            <GatewayHeader
              gateway="bonifico"
              icon={Banknote}
              title="Bonifico Bancario"
              description="Pagamenti tramite bonifico SEPA"
            />
            <CardContent className="space-y-6">
              {settings.bonifico?.enabled && (
                <>
                  <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800 dark:text-amber-200">Gestione Manuale</p>
                        <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                          I bonifici richiedono verifica manuale. Gli ordini resteranno in stato "In attesa pagamento" 
                          fino alla conferma dell'accredito.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>IBAN</Label>
                      <Input
                        value={(settings.bonifico as any).iban || ''}
                        onChange={(e) => updateGateway('bonifico', 'iban', e.target.value)}
                        placeholder="IT60X0542811101000000123456"
                        data-testid="bonifico-iban-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Intestatario Conto</Label>
                      <Input
                        value={(settings.bonifico as any).account_holder || ''}
                        onChange={(e) => updateGateway('bonifico', 'account_holder', e.target.value)}
                        placeholder="Ragione Sociale Srl"
                        data-testid="bonifico-holder-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>BIC/SWIFT</Label>
                      <Input
                        value={(settings.bonifico as any).bic || ''}
                        onChange={(e) => updateGateway('bonifico', 'bic', e.target.value)}
                        placeholder="BCITITMM"
                        data-testid="bonifico-bic-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Banca</Label>
                      <Input
                        value={(settings.bonifico as any).bank_name || ''}
                        onChange={(e) => updateGateway('bonifico', 'bank_name', e.target.value)}
                        placeholder="Nome della Banca"
                        data-testid="bonifico-bank-input"
                      />
                    </div>
                  </div>
                </>
              )}

              {!settings.bonifico?.enabled && (
                <div className="text-center py-8 text-muted-foreground">
                  <Banknote className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Abilita il bonifico per accettare pagamenti bancari</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminPaymentsPage;
